import React, { useEffect, useMemo, useState } from 'react';

const Icon = ({ path, className = 'w-6 h-6' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

const Badge = ({ children, tone = 'slate' }) => {
  const tones = {
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    cyan: 'border-cyan-200 bg-cyan-50 text-cyan-700',
    violet: 'border-violet-200 bg-violet-50 text-violet-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    rose: 'border-rose-200 bg-rose-50 text-rose-700'
  };

  return (
    <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${tones[tone] || tones.slate}`}>
      {children}
    </span>
  );
};

const StatCard = ({ title, value, description, tone = 'slate' }) => {
  const tones = {
    slate: 'from-slate-900 via-slate-800 to-slate-900 text-white shadow-slate-300/40',
    blue: 'from-blue-600 via-cyan-500 to-sky-500 text-white shadow-cyan-300/40',
    violet: 'from-violet-600 via-fuchsia-500 to-purple-500 text-white shadow-violet-300/40',
    emerald: 'from-emerald-600 via-teal-500 to-cyan-500 text-white shadow-emerald-300/40'
  };

  return (
    <div className={`rounded-[26px] bg-gradient-to-br p-4 shadow-xl ${tones[tone] || tones.slate}`}>
      <p className="text-[11px] font-black uppercase tracking-[0.22em] text-white/70">{title}</p>
      <p className="mt-3 text-3xl font-black tracking-[-0.04em]">{value}</p>
      {description ? <p className="mt-2 text-xs text-white/80">{description}</p> : null}
    </div>
  );
};

const InputShell = ({ label, children, hint = '' }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-semibold text-slate-700">{label}</span>
    {children}
    {hint ? <span className="mt-2 block text-xs text-slate-500">{hint}</span> : null}
  </label>
);

const formatTransportLabel = (type) => {
  const map = {
    moto: 'Moto',
    camioneta: 'Camioneta',
    auto: 'Auto',
    camion: 'Camión'
  };
  return map[type] || type;
};

export default function ReportsSettingsView({
  settings,
  onSave,
  onResetRequest,
  showMessage,
  formatCurrency,
  normalizeLiquidationSettings,
  DEFAULT_LIQUIDATION_SETTINGS
}) {
  const [costModel, setCostModel] = useState(settings.costModel || 'perKm');
  const [pricePerKm, setPricePerKm] = useState(settings.pricePerKm || 0);
  const [costScales, setCostScales] = useState(
    settings.costScales || [{ from: 0, to: 10, price: 0 }]
  );

  const [liquidationSettings, setLiquidationSettings] = useState(
    settings.liquidationSettings ||
      normalizeLiquidationSettings(settings) ||
      DEFAULT_LIQUIDATION_SETTINGS
  );

  useEffect(() => {
    setCostModel(settings.costModel || 'perKm');
    setPricePerKm(settings.pricePerKm || 0);
    setCostScales(settings.costScales || [{ from: 0, to: 10, price: 0 }]);
    setLiquidationSettings(
      settings.liquidationSettings ||
        normalizeLiquidationSettings(settings) ||
        DEFAULT_LIQUIDATION_SETTINGS
    );
  }, [settings, normalizeLiquidationSettings, DEFAULT_LIQUIDATION_SETTINGS]);

  const handleAddScale = () => {
    const lastScale = costScales[costScales.length - 1];
    setCostScales([
      ...costScales,
      {
        from: Number(lastScale?.to ?? 0) || 0,
        to: (Number(lastScale?.to ?? 0) || 0) + 10,
        price: 0
      }
    ]);
  };

  const handleScaleChange = (index, field, value) => {
    const newScales = [...costScales];
    newScales[index][field] = field === 'to' && value === '' ? '' : parseFloat(value) || 0;
    setCostScales(newScales);
  };

  const handleRemoveScale = (index) => {
    if (costScales.length > 1) {
      const newScales = costScales.filter((_, i) => i !== index);
      setCostScales(newScales);
    }
  };

  const handleTransportSettingChange = (transportType, field, value) => {
    setLiquidationSettings((prev) => ({
      ...prev,
      transports: {
        ...prev.transports,
        [transportType]: {
          ...prev.transports[transportType],
          [field]: field === 'costModel' ? value : parseFloat(value) || 0
        }
      }
    }));
  };

  const handleSaveSettings = () => {
    onSave({
      costModel,
      pricePerKm,
      costScales,
      liquidationSettings
    });
  };

  const transportTypes = ['moto', 'camioneta', 'auto', 'camion'];

  const stats = useMemo(() => {
    const activeModelLabel = costModel === 'byScale' ? 'Escalas' : 'Por Km';
    const configuredTransports = transportTypes.filter((type) => {
      const typeConfig = liquidationSettings?.transports?.[type];
      return Boolean(
        Number(typeConfig?.pricePerKm || 0) > 0 ||
        Number(typeConfig?.baseRouteFee || 0) > 0 ||
        Number(typeConfig?.minimumRouteFee || 0) > 0 ||
        (Array.isArray(typeConfig?.costScales) && typeConfig.costScales.length > 0)
      );
    }).length;

    return {
      activeModelLabel,
      configuredTransports,
      fallbackPerKm: Number(settings.pricePerKm || 0),
      scaleCount: costScales.length
    };
  }, [costModel, costScales.length, liquidationSettings?.transports, settings.pricePerKm]);

  return (
    <div className="h-full overflow-y-auto pr-1">
      <div className="space-y-6">
        <section className="relative overflow-hidden rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.12),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.08),transparent_28%)]" />
          <div className="relative flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-3xl">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="violet">Liquidación</Badge>
                <Badge tone="slate">Configuración activa</Badge>
              </div>
              <h3 className="mt-4 text-3xl font-black tracking-[-0.04em] text-slate-900 md:text-4xl">
                Ajustá tarifas, respaldos y reglas por tipo de transporte
              </h3>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600 md:text-base">
                Esta vista concentra el respaldo global y las tarifas específicas para que la operación mantenga coherencia entre rutas, reportes y liquidación.
              </p>
            </div>

            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              <Icon path="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" className="h-4 w-4" />
              <span>Guardá cambios cuando cierres la revisión</span>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard title="Modelo activo" value={stats.activeModelLabel} description="Respaldo global vigente" tone="violet" />
          <StatCard title="Valor fallback" value={`$ ${formatCurrency(stats.fallbackPerKm)}`} description="Precio por km de respaldo" tone="blue" />
          <StatCard title="Escalas" value={stats.scaleCount} description="Tramos configurados" tone="slate" />
          <StatCard title="Transportes" value={stats.configuredTransports} description="Con reglas propias" tone="emerald" />
        </section>

        <section className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr,1.4fr]">
          <div className="space-y-6">
            <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">Respaldo global</p>
                  <h4 className="mt-2 text-2xl font-bold tracking-[-0.03em] text-slate-900">Configuración de seguridad</h4>
                  <p className="mt-2 text-sm text-slate-500">
                    Sigue vigente para rutas legacy o escenarios donde falte el tipo de transporte.
                  </p>
                </div>

                <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-right">
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-600">Referencia actual</p>
                  <p className="mt-2 text-2xl font-black text-blue-700">$ {formatCurrency(settings.pricePerKm || 0)}</p>
                  <p className="text-xs text-blue-600">por km</p>
                </div>
              </div>
            </div>

            <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">Modelo global</p>
              <h4 className="mt-2 text-2xl font-bold tracking-[-0.03em] text-slate-900">Cómo se calcula el respaldo</h4>

              <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setCostModel('perKm')}
                  className={`rounded-[22px] border px-4 py-4 text-left transition ${costModel === 'perKm' ? 'border-blue-300 bg-blue-50 shadow-sm' : 'border-slate-200 bg-slate-50 hover:bg-white'}`}
                >
                  <p className="text-sm font-bold text-slate-900">Por kilómetro fijo</p>
                  <p className="mt-1 text-sm text-slate-500">Una tarifa constante para cualquier distancia.</p>
                </button>

                <button
                  type="button"
                  onClick={() => setCostModel('byScale')}
                  className={`rounded-[22px] border px-4 py-4 text-left transition ${costModel === 'byScale' ? 'border-violet-300 bg-violet-50 shadow-sm' : 'border-slate-200 bg-slate-50 hover:bg-white'}`}
                >
                  <p className="text-sm font-bold text-slate-900">Por escala de distancia</p>
                  <p className="mt-1 text-sm text-slate-500">Tramos con precio según kilometraje.</p>
                </button>
              </div>

              {costModel === 'perKm' ? (
                <div className="mt-5 rounded-[24px] border border-blue-200 bg-blue-50/70 p-5 animate-fade-in-down">
                  <InputShell label="Nuevo precio por kilómetro ($)" hint="Se usa como respaldo global cuando no hay configuración por tipo.">
                    <input
                      type="number"
                      value={pricePerKm}
                      onChange={(e) => setPricePerKm(parseFloat(e.target.value) || 0)}
                      className="w-full rounded-2xl border border-blue-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                    />
                  </InputShell>
                </div>
              ) : (
                <div className="mt-5 rounded-[24px] border border-violet-200 bg-violet-50/70 p-5 animate-fade-in-down">
                  <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                    <div>
                      <p className="text-lg font-bold text-slate-900">Escalas globales</p>
                      <p className="mt-1 text-sm text-slate-500">Definí tramos para que el respaldo tome precio según distancia.</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddScale}
                      className="inline-flex items-center gap-2 rounded-2xl border border-violet-200 bg-white px-4 py-3 text-sm font-bold text-violet-700 transition hover:bg-violet-100"
                    >
                      <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                      Añadir escala
                    </button>
                  </div>

                  <div className="mt-4 space-y-3">
                    {costScales.map((scale, index) => (
                      <div key={index} className="rounded-[22px] border border-violet-100 bg-white p-4 shadow-sm">
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <span className="rounded-full bg-violet-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-violet-700">
                            Escala {index + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveScale(index)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-rose-200 text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40"
                            disabled={costScales.length <= 1}
                            title="Eliminar escala"
                          >
                            <Icon path="M6 18L18 6M6 6l12 12" className="h-4 w-4" />
                          </button>
                        </div>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                          <InputShell label="Desde (km)">
                            <input
                              type="number"
                              value={scale.from}
                              onChange={(e) => handleScaleChange(index, 'from', e.target.value)}
                              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-violet-300 focus:ring-4 focus:ring-violet-100"
                              placeholder="Desde"
                            />
                          </InputShell>
                          <InputShell label="Hasta (km)" hint="Podés dejar el último tramo abierto ajustándolo luego.">
                            <input
                              type="number"
                              value={scale.to}
                              onChange={(e) => handleScaleChange(index, 'to', e.target.value)}
                              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-violet-300 focus:ring-4 focus:ring-violet-100"
                              placeholder="Hasta"
                            />
                          </InputShell>
                          <InputShell label="Precio ($)">
                            <input
                              type="number"
                              value={scale.price}
                              onChange={(e) => handleScaleChange(index, 'price', e.target.value)}
                              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-violet-300 focus:ring-4 focus:ring-violet-100"
                              placeholder="Precio"
                            />
                          </InputShell>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">Tarifas por transporte</p>
              <h4 className="mt-2 text-2xl font-bold tracking-[-0.03em] text-slate-900">Configuración específica por unidad</h4>
              <p className="mt-2 text-sm text-slate-500">
                Estas reglas tienen prioridad cuando la ruta ya conoce el tipo de transporte que la ejecuta.
              </p>

              <div className="mt-5 space-y-4">
                {transportTypes.map((type) => {
                  const typeConfig = liquidationSettings?.transports?.[type] || {
                    costModel: 'perKm',
                    pricePerKm: 0,
                    baseRouteFee: 0,
                    minimumRouteFee: 0
                  };

                  return (
                    <div key={type} className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 shadow-sm">
                      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div>
                          <Badge tone="cyan">{formatTransportLabel(type)}</Badge>
                          <p className="mt-3 text-sm text-slate-500">Definí el modelo y sus valores para este transporte en particular.</p>
                        </div>
                        <span className="inline-flex rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                          {typeConfig.costModel === 'byScale' ? 'Escala' : 'Por Km'}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <InputShell label="Modelo de costo">
                          <select
                            value={typeConfig.costModel}
                            onChange={(e) => handleTransportSettingChange(type, 'costModel', e.target.value)}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-cyan-300 focus:ring-4 focus:ring-cyan-100"
                          >
                            <option value="perKm">Por km</option>
                            <option value="byScale">Por escala</option>
                          </select>
                        </InputShell>

                        <InputShell label="Precio por km">
                          <input
                            type="number"
                            value={typeConfig.pricePerKm}
                            onChange={(e) => handleTransportSettingChange(type, 'pricePerKm', e.target.value)}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-cyan-300 focus:ring-4 focus:ring-cyan-100"
                          />
                        </InputShell>

                        <InputShell label="Cargo base por ruta">
                          <input
                            type="number"
                            value={typeConfig.baseRouteFee}
                            onChange={(e) => handleTransportSettingChange(type, 'baseRouteFee', e.target.value)}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-cyan-300 focus:ring-4 focus:ring-cyan-100"
                          />
                        </InputShell>

                        <InputShell label="Mínimo por ruta">
                          <input
                            type="number"
                            value={typeConfig.minimumRouteFee}
                            onChange={(e) => handleTransportSettingChange(type, 'minimumRouteFee', e.target.value)}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-cyan-300 focus:ring-4 focus:ring-cyan-100"
                          />
                        </InputShell>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-[28px] border border-rose-200 bg-rose-50/70 p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="mt-1 flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-rose-600 shadow-sm">
                  <Icon path="M12 9v3.75m9.303 3.376c.866 1.5-.217 3.374-1.948 3.374H4.645c-1.73 0-2.813-1.874-1.948-3.374L10.051 3.378c.866-1.5 3.032-1.5 3.898 0l7.354 12.748zM12 15.75h.007v.008H12v-.008z" className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-rose-600">Configuración avanzada</p>
                  <h4 className="mt-2 text-2xl font-bold tracking-[-0.03em] text-slate-900">Resetear historial operativo</h4>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    Esta acción elimina permanentemente todas las rutas del historial. Usala sólo cuando realmente quieras reiniciar la base operativa.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={onResetRequest}
                className="mt-5 inline-flex items-center gap-2 rounded-2xl border border-rose-200 bg-white px-4 py-3 text-sm font-bold text-rose-700 transition hover:bg-rose-100"
              >
                <Icon path="M6 18L18 6M6 6l12 12" className="h-4 w-4" />
                Resetear datos de rutas
              </button>
            </div>
          </div>
        </section>

        <button
          type="button"
          onClick={handleSaveSettings}
          className="w-full rounded-[26px] bg-blue-600 px-4 py-4 text-sm font-black uppercase tracking-[0.18em] text-white shadow-xl shadow-blue-200 transition hover:bg-blue-700"
        >
          Guardar cambios de liquidación
        </button>
      </div>
    </div>
  );
}

export const ConfirmationModal = ({ show, onClose, onConfirm, title, children }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-[2px]">
      <div className="w-full max-w-md rounded-[28px] border border-slate-200 bg-white p-6 shadow-2xl animate-fade-in-down">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
            <Icon path="M12 9v3.75m9.303 3.376c.866 1.5-.217 3.374-1.948 3.374H4.645c-1.73 0-2.813-1.874-1.948-3.374L10.051 3.378c.866-1.5 3.032-1.5 3.898 0l7.354 12.748zM12 15.75h.007v.008H12v-.008z" className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-900">{title}</h3>
            <div className="mt-2 text-sm leading-relaxed text-slate-600">{children}</div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-200"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-2xl bg-rose-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-rose-700"
          >
            Confirmar eliminación
          </button>
        </div>
      </div>
    </div>
  );
};
